import React from 'react';
import clsx from 'clsx';
import { IconButton, makeStyles, Theme } from '@material-ui/core';
import { colorSet } from '@components/Provider';
import { CustomizedTypography } from '@components/Customized';
import AppConfig from '@config';
import * as assets from '@assets';
import { UploadedFileWithProperty } from './RequestFormFileInput';
import { Document, CloseFilled } from '@carbon/icons-react';

export interface DesignReferenceFileListEntryProps
  extends UploadedFileWithProperty {
  onClickDeleteFile: (fileId: string) => void;
  onUpdateFile: (
    fileId: string,
    updateFileData: Partial<UploadedFileWithProperty>
  ) => void;
}

const DesignReferenceFileListEntry: React.FC<
  DesignReferenceFileListEntryProps
> = (file) => {
  const { fileId, fileName, onClickDeleteFile } = file;
  const { uploadedFileEntry } = useReferenceFileListEntryStyles();
  const imageExtensions = ['png', 'jpg', 'jpeg'];
  const extension = fileName.split('.').pop();
  const isImageFile = imageExtensions.includes(extension as string);

  return (
    <div className={uploadedFileEntry}>
      <div className="fileName">
        <div className="filename-with-icon">
          <div
            className={clsx([
              'file-thumbnail',
              {
                withImage: isImageFile
              }
            ])}
          >
            {isImageFile ? (
              <img
                src={`${AppConfig.fileApiUrl}/file/${fileId}`}
                alt={fileName}
                width="38"
                height="38"
                onError={(e) => {
                  e.currentTarget.src = assets.DefaultThumbnail;
                }}
              />
            ) : (
              <Document size={20} color={colorSet.gray700} />
            )}
          </div>

          <CustomizedTypography variant="body2" color="textPrimary" noWrap>
            {fileName}
          </CustomizedTypography>
        </div>
        <IconButton
          onClick={() => onClickDeleteFile(fileId)}
          data-cy={`rfq-design-referenceFile-delete-${fileId}`}
        >
          <CloseFilled size={20} color={colorSet.gray600} />
        </IconButton>
      </div>
    </div>
  );
};

export default DesignReferenceFileListEntry;

const useReferenceFileListEntryStyles = makeStyles<Theme>((theme) => ({
  uploadedFileEntry: {
    position: 'relative',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
    height: 56,
    borderRadius: 8,
    backgroundColor: colorSet.gray100,
    padding: '8px',
    maxWidth: 228,
    [theme.breakpoints.down('sm')]: {
      maxWidth: () => 'unset',
      flexDirection: 'column',
      justifyContent: 'center',
      height: 56
    },
    '& .fileName': {
      width: '100%',
      position: 'relative',
      display: 'flex',
      gap: '9px',
      '& .filename-with-icon': {
        display: 'flex',
        gap: '8px',
        width: 'calc(100% - 40px)',
        alignItems: 'center',
        '& .file-thumbnail': {
          minWidth: 40,
          height: 40,
          borderRadius: '8px',
          overflow: 'hidden',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          border: `1px solid ${colorSet.gray400}`,
          '&.withImage': {
            borderColor: colorSet.gray700
          }
        }
      },
      [theme.breakpoints.down('sm')]: {
        height: '20px',
        justifyContent: 'space-between',
        alignItems: 'center',
        '& .filename-with-icon': {
          width: 'calc(100% - 64px)'
        }
      }
    },
    '& .fileProperties': {
      display: 'flex',
      alignItems: 'center',
      gap: '4px',
      [theme.breakpoints.down('sm')]: {
        width: ' 100%',
        gap: '8px'
      }
    },
    '& button': {
      width: 32,
      height: 32,
      marginLeft: '2px',
      alignSelf: 'center',
      padding: 6
    }
  },
  input: {
    maxWidth: 110,
    borderRadius: '8px',
    backgroundColor: colorSet.gray000,
    [theme.breakpoints.down('sm')]: {
      flex: 1,
      maxWidth: 'unset'
    },
    '& input': {
      borderRadius: '8px',
      padding: '10px 4px 10px 12px',
      textAlign: 'right',
      '&::placeholder': {
        color: colorSet.gray500
      }
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: colorSet.gray400
    }
  }
}));
